import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadCloud, Loader2, CheckCircle2 } from "lucide-react";

const CHUNK = 4 * 1024 * 1024; // 4MB chunks bypass proxy body limits

export const VideoUploader = ({ onDone, compact = false }) => {
  const [progress, setProgress] = useState(-1);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 300 * 1024 * 1024) { toast.error("Video is over 300MB — please compress it first."); return; }
    const uploadId = `${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    const total = Math.ceil(file.size / CHUNK);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        await api.post(`/admin/video/chunk?upload_id=${uploadId}&index=${i}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const r = await api.post("/admin/video/finalize", { upload_id: uploadId, content_type: file.type || "video/mp4" });
      setProgress(100);
      toast.success("Demo video uploaded — it's now live on the landing page.");
      onDone?.(r.data.demo_video_url);
      setTimeout(() => setProgress(-1), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Try a smaller file.");
      setProgress(-1);
    }
  };

  const busy = progress >= 0 && progress < 100;
  return (
    <div data-testid="video-uploader">
      <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime" hidden
        data-testid="video-file-input"
        onChange={(e) => upload(e.target.files?.[0])} />
      <Button type="button" size={compact ? "sm" : "default"} disabled={busy}
        onClick={() => inputRef.current?.click()} data-testid="video-upload-btn"
        className="bg-primary hover:bg-primary/90 text-white">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : progress === 100 ? <CheckCircle2 className="h-4 w-4 mr-2" /> : <UploadCloud className="h-4 w-4 mr-2" />}
        {busy ? `Uploading ${progress}%` : progress === 100 ? "Uploaded" : "Upload demo video"}
      </Button>
      {busy && (
        <div className="mt-2 h-1.5 w-full max-w-xs rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
};
